export const canonicalOrigin = 'https://www.lindseyenterprisesaz.com';

type JsonLdNode = Record<string, unknown>;

type SeoConfig = {
  title: string;
  description: string;
  path: string;
  robots?: string;
  ogImage?: string;
  imageAlt?: string;
};

type ServiceSeoConfig = SeoConfig & {
  serviceName: string;
  serviceType: string;
  schemaDescription: string;
  audienceType: string;
};

const siteName = 'Lindsey Enterprises';
const defaultRobots = 'index, follow, max-image-preview:large';
const defaultImage = `${canonicalOrigin}/images/hero.webp`;
const defaultImageAlt = 'Lindsey Enterprises luxury cleaning and detailing work in Tucson';
const logoUrl = `${canonicalOrigin}/images/logo.png`;
const businessId = `${canonicalOrigin}/#localbusiness`;
const websiteId = `${canonicalOrigin}/#website`;
const contactPointId = `${canonicalOrigin}/#contact`;

const serviceAreas = [
  'Tucson',
  'Marana',
  'Oro Valley',
  'Vail',
  'Sahuarita',
  'Green Valley',
  'Sierra Vista',
];

const defaultSeo: SeoConfig = {
  title: 'Lindsey Enterprises | Tucson Cleaning & Auto Detailing',
  description:
    'Protect the vehicles, floors, fabrics, and stone you care about. Lindsey Enterprises provides IICRC-certified cleaning, detailing, and restoration in Tucson.',
  path: '/',
  imageAlt: defaultImageAlt,
};

const serviceSeo: Record<string, ServiceSeoConfig> = {
  'auto-detailing': {
    title: 'Auto & Marine Detailing Tucson | Lindsey Enterprises',
    description:
      'Restore clarity, depth, and protection with Tucson auto and marine detailing for paint correction, ceramic coatings, interiors, leather, and maintenance care.',
    path: '/services/auto-detailing',
    serviceName: 'Auto and Marine Detailing',
    serviceType: 'Auto detailing, marine detailing, paint correction, ceramic coating, interior restoration',
    schemaDescription:
      'Luxury auto and marine detailing in Tucson, including paint correction, ceramic coatings, leather care, interior restoration, and maintenance programs.',
    audienceType: 'Vehicle, luxury auto, classic car, exotic car, and marine vessel owners in Southern Arizona',
    imageAlt: 'Detailed luxury vehicle prepared by Lindsey Enterprises in Tucson',
  },
  'carpet-cleaning': {
    title: 'Carpet & Rug Cleaning Tucson | Lindsey Enterprises',
    description:
      'Embedded desert grit shortens carpet life. Lindsey Enterprises provides IICRC-certified carpet and area rug cleaning with truck-mounted extraction.',
    path: '/services/carpet-cleaning',
    serviceName: 'Carpet and Area Rug Cleaning',
    serviceType: 'Carpet cleaning, area rug cleaning, odor removal, carpet protection',
    schemaDescription:
      'IICRC-certified carpet and area rug cleaning in Tucson using professional extraction, soil treatment, odor removal, and optional fiber protection.',
    audienceType: 'Homeowners, estate managers, and select commercial property owners in Southern Arizona',
    imageAlt: 'Freshly cleaned residential carpet and area rug care by Lindsey Enterprises',
  },
  'tile-grout': {
    title: 'Tile, Stone & Grout Cleaning Tucson | Lindsey Enterprises',
    description:
      'Grout and stone hold soil that household cleaning leaves behind. Restore tile, stone, VCT, vinyl, and grout with IICRC-certified Tucson care.',
    path: '/services/tile-grout',
    serviceName: 'Tile, Stone, and Grout Cleaning',
    serviceType: 'Tile cleaning, grout cleaning, natural stone cleaning, VCT refinishing, sealing',
    schemaDescription:
      'Professional tile, grout, natural stone, VCT, and vinyl cleaning, restoration, refinishing, and sealing for Tucson-area properties.',
    audienceType: 'Residential and select commercial property owners with tile, stone, grout, VCT, or vinyl surfaces',
    imageAlt: 'Restored stone tile and grout surface cleaned by Lindsey Enterprises',
  },
  'upholstery-leather': {
    title: 'Upholstery & Leather Cleaning Tucson | Lindsey Enterprises',
    description:
      'Fine fabrics and leather need the right method. Schedule Tucson upholstery, fabric, and leather cleaning, conditioning, and protection.',
    path: '/services/upholstery-leather',
    serviceName: 'Upholstery and Leather Cleaning',
    serviceType: 'Upholstery cleaning, fabric protection, leather cleaning, leather conditioning',
    schemaDescription:
      'IICRC-certified upholstery, fabric, and leather cleaning in Tucson with conditioning and protection for fine furnishings, vehicles, and interiors.',
    audienceType: 'Homeowners and vehicle owners caring for fine upholstery, leather, and specialty fabrics',
    imageAlt: 'Luxury leather and upholstery care by Lindsey Enterprises',
  },
};

const routeSeo: Record<string, SeoConfig> = {
  '/': defaultSeo,
  '/services': {
    title: 'Cleaning & Detailing Services Tucson | Lindsey Enterprises',
    description:
      'Compare services for auto detailing, carpet, tile, stone, grout, upholstery, and leather care. Learn what each surface needs before you schedule.',
    path: '/services',
    imageAlt: 'Lindsey Enterprises cleaning and detailing service portfolio',
  },
  '/why-it-matters': {
    title: 'Why Professional Cleaning Matters | Lindsey Enterprises',
    description:
      'Learn how professional surface care protects resale value, indoor air, carpet fibers, grout, stone, vehicle paint, and leather in Tucson conditions.',
    path: '/why-it-matters',
    imageAlt: 'Professional cleaning and detailing protects homes and vehicles in Tucson',
  },
  '/gallery': {
    title: 'Gallery | Tucson Cleaning & Detailing Work',
    description:
      'See vehicles, interiors, carpet, stone, tile, upholstery, leather, and marine surfaces entrusted to Lindsey Enterprises across Southern Arizona.',
    path: '/gallery',
    imageAlt: 'Gallery of Lindsey Enterprises cleaning and detailing work',
  },
  '/contact': {
    title: 'Schedule Cleaning or Detailing Service | Lindsey Enterprises',
    description:
      'Tell us about your vehicle, home, or surface-care project. Request Lindsey Enterprises service for Tucson, Marana, Oro Valley, Vail, and nearby areas.',
    path: '/contact',
    imageAlt: 'Schedule Lindsey Enterprises cleaning and detailing service in Tucson',
  },
};

const serviceList = Object.values(serviceSeo);

const isServiceSeo = (seo: SeoConfig): seo is ServiceSeoConfig => 'serviceName' in seo;

const normalizePath = (pathname: string) => {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/, '');
};

export const canonicalUrlForPath = (pathname: string) => {
  const normalizedPath = normalizePath(pathname);
  return `${canonicalOrigin}${normalizedPath === '/' ? '/' : normalizedPath}`;
};

export function getSeoForPath(pathname: string): SeoConfig {
  const normalizedPath = normalizePath(pathname);
  const serviceMatch = normalizedPath.match(/^\/services\/([^/]+)$/);

  if (serviceMatch) {
    return serviceSeo[serviceMatch[1]] ?? {
      title: 'Service Not Found | Lindsey Enterprises',
      description: 'The requested Lindsey Enterprises service page could not be found.',
      path: normalizedPath,
      robots: 'noindex, follow',
    };
  }

  return routeSeo[normalizedPath] ?? {
    title: 'Page Not Found | Lindsey Enterprises',
    description: 'The requested Lindsey Enterprises page could not be found.',
    path: normalizedPath,
    robots: 'noindex, follow',
  };
}

const serviceAreaNodes = serviceAreas.map((name) => ({
  '@type': 'City',
  name,
  address: {
    '@type': 'PostalAddress',
    addressRegion: 'AZ',
    addressCountry: 'US',
  },
}));

const businessNode: JsonLdNode = {
  '@type': 'LocalBusiness',
  '@id': businessId,
  name: siteName,
  alternateName: [
    'Illuminations Detail',
    'Illuminations Mobile Detail',
    "Lindsey's Carpet & Tile Care",
  ],
  url: `${canonicalOrigin}/`,
  telephone: '+1-520-325-0576',
  email: 'info@lindseyenterprisesaz.com',
  image: [defaultImage],
  logo: logoUrl,
  description:
    'IICRC-certified cleaning, detailing, and restoration for vehicles, carpet, rugs, tile, stone, grout, upholstery, leather, and marine surfaces in Tucson and Southern Arizona.',
  foundingDate: '1994',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Tucson',
    addressRegion: 'AZ',
    addressCountry: 'US',
  },
  areaServed: serviceAreaNodes,
  contactPoint: {
    '@type': 'ContactPoint',
    '@id': contactPointId,
    telephone: '+1-520-325-0576',
    email: 'info@lindseyenterprisesaz.com',
    contactType: 'customer service',
    areaServed: 'US-AZ',
    availableLanguage: 'English',
  },
  knowsAbout: [
    'Auto detailing',
    'Marine detailing',
    'Paint correction',
    'Ceramic coating',
    'Carpet cleaning',
    'Area rug cleaning',
    'Tile and grout cleaning',
    'Natural stone care',
    'Upholstery cleaning',
    'Leather conditioning',
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Cleaning and detailing services',
    itemListElement: serviceList.map((service, index) => ({
      '@type': 'Offer',
      position: index + 1,
      url: canonicalUrlForPath(service.path),
      itemOffered: {
        '@type': 'Service',
        '@id': `${canonicalUrlForPath(service.path)}#service`,
        name: service.serviceName,
        serviceType: service.serviceType,
        description: service.schemaDescription,
        provider: { '@id': businessId },
        areaServed: serviceAreaNodes,
      },
    })),
  },
};

const websiteNode: JsonLdNode = {
  '@type': 'WebSite',
  '@id': websiteId,
  url: `${canonicalOrigin}/`,
  name: siteName,
  publisher: { '@id': businessId },
  inLanguage: 'en-US',
};

const serviceListNode = (canonicalUrl: string): JsonLdNode => ({
  '@type': 'ItemList',
  '@id': `${canonicalUrl}#services`,
  name: 'Lindsey Enterprises cleaning and detailing services',
  itemListElement: serviceList.map((service, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: service.serviceName,
    url: canonicalUrlForPath(service.path),
  })),
});

const faqNode = (canonicalUrl: string): JsonLdNode => ({
  '@type': 'FAQPage',
  '@id': `${canonicalUrl}#faq`,
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Why is professional carpet cleaning better than renting a machine?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Rental machines use cold or lukewarm water and have limited suction. A truck-mounted system heats water to optimal temperatures and generates stronger extraction, removing more soil and leaving less moisture behind.',
      },
    },
    {
      '@type': 'Question',
      name: 'Will professional cleaning damage my natural stone or tile?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No, when the work is performed by trained professionals who understand the surface. Lindsey Enterprises uses surface-appropriate agents and techniques for stone, tile, grout, vinyl, and VCT.',
      },
    },
    {
      '@type': 'Question',
      name: 'How does paint correction work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Paint correction removes surface defects such as swirl marks, scratches, and oxidation through machine polishing with selected compounds and polishes, improving gloss and clarity before protection is applied.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need ceramic coating after paint correction?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Paint correction restores the finish. Ceramic coating or polymer protection helps preserve that corrected surface against UV exposure, contaminants, and light wear.',
      },
    },
    {
      '@type': 'Question',
      name: 'How often should leather be professionally cleaned and conditioned?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'In Tucson, leather benefits from professional cleaning and conditioning about every six months, especially when exposed to direct sunlight or dry indoor conditions.',
      },
    },
  ],
});

const serviceNode = (service: ServiceSeoConfig): JsonLdNode => {
  const url = canonicalUrlForPath(service.path);

  return {
    '@type': 'Service',
    '@id': `${url}#service`,
    name: service.serviceName,
    serviceType: service.serviceType,
    description: service.schemaDescription,
    provider: { '@id': businessId },
    areaServed: serviceAreaNodes,
    audience: {
      '@type': 'Audience',
      audienceType: service.audienceType,
    },
    url,
  };
};

const breadcrumbItemsForPath = (pathname: string, seo: SeoConfig) => {
  const normalizedPath = normalizePath(pathname);
  const items = [
    {
      name: 'Home',
      item: `${canonicalOrigin}/`,
    },
  ];

  if (normalizedPath.startsWith('/services/') && isServiceSeo(seo)) {
    items.push({
      name: 'Services',
      item: canonicalUrlForPath('/services'),
    });
    items.push({
      name: seo.serviceName,
      item: canonicalUrlForPath(seo.path),
    });
    return items;
  }

  if (normalizedPath !== '/') {
    items.push({
      name: seo.title.replace(` | ${siteName}`, '').replace(' | Tucson Cleaning & Detailing Work', ''),
      item: canonicalUrlForPath(seo.path),
    });
  }

  return items;
};

const breadcrumbNode = (pathname: string, seo: SeoConfig): JsonLdNode => {
  const canonicalUrl = canonicalUrlForPath(seo.path);

  return {
    '@type': 'BreadcrumbList',
    '@id': `${canonicalUrl}#breadcrumb`,
    itemListElement: breadcrumbItemsForPath(pathname, seo).map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.item,
    })),
  };
};

const webPageTypesForPath = (pathname: string) => {
  const normalizedPath = normalizePath(pathname);

  if (normalizedPath === '/contact') return ['WebPage', 'ContactPage'];
  if (normalizedPath === '/gallery') return ['WebPage', 'CollectionPage'];
  if (normalizedPath === '/services') return ['WebPage', 'CollectionPage'];
  return 'WebPage';
};

const mainEntityForPath = (pathname: string, seo: SeoConfig) => {
  const normalizedPath = normalizePath(pathname);
  const canonicalUrl = canonicalUrlForPath(seo.path);

  if (isServiceSeo(seo)) return { '@id': `${canonicalUrl}#service` };
  if (normalizedPath === '/services') return { '@id': `${canonicalUrl}#services` };
  if (normalizedPath === '/why-it-matters') return { '@id': `${canonicalUrl}#faq` };
  if (normalizedPath === '/contact') return { '@id': contactPointId };
  return { '@id': businessId };
};

const webPageNode = (pathname: string, seo: SeoConfig): JsonLdNode => {
  const canonicalUrl = canonicalUrlForPath(seo.path);

  return {
    '@type': webPageTypesForPath(pathname),
    '@id': `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: seo.title,
    description: seo.description,
    isPartOf: { '@id': websiteId },
    about: { '@id': businessId },
    mainEntity: mainEntityForPath(pathname, seo),
    breadcrumb: { '@id': `${canonicalUrl}#breadcrumb` },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: seo.ogImage ?? defaultImage,
      caption: seo.imageAlt ?? defaultImageAlt,
    },
    inLanguage: 'en-US',
  };
};

export function getStructuredDataForPath(pathname: string): JsonLdNode {
  const seo = getSeoForPath(pathname);
  const normalizedPath = normalizePath(pathname);
  const canonicalUrl = canonicalUrlForPath(seo.path);
  const graph: JsonLdNode[] = [
    businessNode,
    websiteNode,
    breadcrumbNode(pathname, seo),
    webPageNode(pathname, seo),
  ];

  if (normalizedPath === '/' || normalizedPath === '/services') {
    graph.push(serviceListNode(canonicalUrl));
  }

  if (normalizedPath === '/why-it-matters') {
    graph.push(faqNode(canonicalUrl));
  }

  if (isServiceSeo(seo)) {
    graph.push(serviceNode(seo));
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

const upsertMeta = (selector: string, create: () => HTMLMetaElement, content: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = create();
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const setNamedMeta = (name: string, content: string) => {
  upsertMeta(`meta[name="${name}"]`, () => {
    const element = document.createElement('meta');
    element.setAttribute('name', name);
    return element;
  }, content);
};

const setPropertyMeta = (property: string, content: string) => {
  upsertMeta(`meta[property="${property}"]`, () => {
    const element = document.createElement('meta');
    element.setAttribute('property', property);
    return element;
  }, content);
};

const setCanonical = (canonicalUrl: string) => {
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', canonicalUrl);
};

const setStructuredData = (pathname: string) => {
  let script = document.head.querySelector<HTMLScriptElement>('script#structured-data[type="application/ld+json"]');
  if (!script) {
    script = document.createElement('script');
    script.setAttribute('id', 'structured-data');
    script.setAttribute('type', 'application/ld+json');
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(getStructuredDataForPath(pathname));
};

export function applySeo(pathname: string) {
  const seo = getSeoForPath(pathname);
  const canonicalUrl = canonicalUrlForPath(seo.path);
  const image = seo.ogImage ?? defaultImage;
  const imageAlt = seo.imageAlt ?? defaultImageAlt;
  const robots = seo.robots ?? defaultRobots;

  document.title = seo.title;

  setNamedMeta('description', seo.description);
  setNamedMeta('robots', robots);
  setNamedMeta('twitter:card', 'summary_large_image');
  setNamedMeta('twitter:title', seo.title);
  setNamedMeta('twitter:description', seo.description);
  setNamedMeta('twitter:image', image);
  setNamedMeta('twitter:image:alt', imageAlt);

  setPropertyMeta('og:title', seo.title);
  setPropertyMeta('og:description', seo.description);
  setPropertyMeta('og:type', 'website');
  setPropertyMeta('og:url', canonicalUrl);
  setPropertyMeta('og:site_name', siteName);
  setPropertyMeta('og:image', image);
  setPropertyMeta('og:image:alt', imageAlt);
  setPropertyMeta('og:locale', 'en_US');

  setCanonical(canonicalUrl);
  setStructuredData(pathname);
}
